/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 *
 * "THE FORM JUST CHANGED SOMETHING I DID NOT CHANGE." — say what, and say why.
 *
 * A few things on the intake write values the operator did not type: the only active policy
 * is selected for them, the grid's columns are seeded from the policy, switching to
 * `Repeats` seeds a weekday, switching to a single shift truncates the rows, a typed `8`
 * becomes `08:00`. Each is deliberate and each is documented at its call site — but from the
 * outside they are indistinguishable from a bug, and this pass began with three reports that
 * were exactly that shape.
 *
 * ⚠️ DEVELOPMENT ONLY, AND SILENT EVERYWHERE ELSE.
 *
 *   • `import.meta.env.DEV` is false in a production build, so `notifyDevChange` compiles to
 *     a function that returns immediately and the notification module is never imported.
 *   • ⚠️ `MODE === 'test'` IS CHECKED SEPARATELY, BECAUSE VITEST SETS `DEV` TO TRUE. Without
 *     that second condition every page test would render notification popups over the DOM it
 *     is asserting against.
 *
 * It is not a logger. `logger`-style output is read after the fact by somebody who already
 * suspects something; this is read by whoever is looking at the screen at the moment it
 * happens, which is when "why did that move?" is actually asked.
 *
 * ⚠️ IT IS ONE LINE, TOP RIGHT, GONE IN FIVE SECONDS. Long enough to catch what moved,
 * short enough that nobody has to dismiss it — and out of the way of the form, which is at
 * the top LEFT of the content column. The reason goes to `console.debug`, because a
 * paragraph in the corner of a form is something to dismiss rather than something to read.
 */

export type DevChange = {
  /** Where it happened, in the operator's words — "Dates", "Force required". */
  where: string;
  /** What changed, in one line — "Weekdays set to Monday". */
  what: string;
  /** Why, in one sentence. This is the part that is actually worth reading. */
  why: string;
};

export const isDevNoticeEnabled = (): boolean => import.meta.env.DEV && import.meta.env.MODE !== 'test';

/** Swappable so a test can assert what WOULD have been announced without rendering it. */
let sink: ((change: DevChange) => void) | undefined;

export const setDevNoticeSink = (next: ((change: DevChange) => void) | undefined): void => {
  sink = next;
};

export const notifyDevChange = (change: DevChange): void => {
  if (sink) {
    sink(change);
    return;
  }
  if (!isDevNoticeEnabled()) return;

  /*
   * ⚠️ THE REASON GOES TO THE CONSOLE, NOT ONTO THE SCREEN.
   *
   * The toast answers "what just moved?" in one glance and gets out of the way; the `why` is
   * a paragraph, and a paragraph in the corner of a form is something to dismiss rather than
   * something to read. It is one `console.debug` away for whoever actually wants it, which
   * is where a developer looks next anyway.
   */
  // eslint-disable-next-line no-console -- development-only, and gated by `isDevNoticeEnabled`.
  console.debug(`[dev] ${change.where} — ${change.what}\n${change.why}`);

  /*
   * Imported lazily so the production bundle never pulls AntD's notification module in on
   * account of a development affordance.
   */
  void import('antd').then(({ notification }) => {
    notification.open({
      /*
       * ⚠️ A STABLE KEY, SO AN IDENTICAL CHANGE REPLACES ITSELF AND RESTARTS ITS FIVE
       * SECONDS. Distinct changes still stack — which is the stacking that matters — while a
       * time field normalising on every blur of six shift rows does not bury the screen in
       * six copies of one sentence.
       */
      key: `${change.where}:${change.what}`,
      message: (
        <span className="text-xs">
          <span className="text-muted-foreground">{change.where}</span> · {change.what}
        </span>
      ),
      placement: 'topRight',
      duration: 5,
      /* Compact: one line, no icon, no close button to aim at for something self-dismissing. */
      icon: null,
      closeIcon: null,
      style: { padding: '8px 12px', width: 'auto', maxWidth: 380 },
    });
  });
};
